import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { handleSupabaseError } from '@/lib/supabaseConnectionTest';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  lastError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: any }>;
  testConnection: () => Promise<boolean>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  connectionStatus: 'disconnected',
  lastError: null,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  signInWithGoogle: async () => ({ error: null }),
  testConnection: async () => false,
  clearError: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const testConnection = async (): Promise<boolean> => {
    try {
      setConnectionStatus('reconnecting');
      const { error } = await supabase.auth.getSession();
      
      if (!error) {
        setConnectionStatus('connected');
        setLastError(null);
        setRetryCount(0);
        return true;
      } else {
        throw error;
      }
    } catch (error) {
      const errorMessage = handleSupabaseError(error, 'connection test');
      setLastError(errorMessage);
      setConnectionStatus('disconnected');
      return false;
    }
  };

  const retryConnection = async () => {
    if (retryCount >= maxRetries) {
      console.warn('Max retry attempts reached');
      return;
    }

    setRetryCount(prev => prev + 1);
    console.log(`Retrying connection... Attempt ${retryCount + 1}/${maxRetries}`);
    
    setTimeout(async () => {
      await testConnection();
    }, Math.pow(2, retryCount) * 1000); // Exponential backoff
  };

  const clearError = () => {
    setLastError(null);
  };

  useEffect(() => {
    // Get initial session with enhanced error handling
    const getInitialSession = async () => {
      try {
        setConnectionStatus('reconnecting');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          const errorMessage = handleSupabaseError(error, 'initial session');
          console.error('Error getting session:', error);
          setLastError(errorMessage);
          setConnectionStatus('disconnected');
          retryConnection();
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          setConnectionStatus('connected');
          setLastError(null);
        }
      } catch (error) {
        const errorMessage = handleSupabaseError(error, 'initial session');
        setLastError(errorMessage);
        setConnectionStatus('disconnected');
        retryConnection();
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes with enhanced error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);

          if (session) {
            setConnectionStatus('connected');
            setLastError(null);
          }

          if (event === 'SIGNED_IN') {
            console.log('User signed in:', session?.user);
            setConnectionStatus('connected');
          } else if (event === 'SIGNED_OUT') {
            console.log('User signed out');
          } else if (event === 'TOKEN_REFRESHED') {
            console.log('Token refreshed successfully');
            setConnectionStatus('connected');
          }
        } catch (error) {
          const errorMessage = handleSupabaseError(error, 'auth state change');
          setLastError(errorMessage);
          console.error('Auth state change error:', error);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setConnectionStatus('reconnecting');
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        const errorMessage = handleSupabaseError(error, 'sign in');
        setLastError(errorMessage);
        setConnectionStatus('disconnected');
      } else {
        setConnectionStatus('connected');
        setLastError(null);
      }
      
      return { error };
    } catch (error) {
      const errorMessage = handleSupabaseError(error, 'sign in');
      setLastError(errorMessage);
      setConnectionStatus('disconnected');
      return { error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setConnectionStatus('reconnecting');
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });
      
      if (error) {
        const errorMessage = handleSupabaseError(error, 'sign up');
        setLastError(errorMessage);
        setConnectionStatus('disconnected');
      } else {
        setConnectionStatus('connected');
        setLastError(null);
      }
      
      return { error };
    } catch (error) {
      const errorMessage = handleSupabaseError(error, 'sign up');
      setLastError(errorMessage);
      setConnectionStatus('disconnected');
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setConnectionStatus('disconnected');
      setLastError(null);
    } catch (error) {
      const errorMessage = handleSupabaseError(error, 'sign out');
      setLastError(errorMessage);
      console.error('Error signing out:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setConnectionStatus('reconnecting');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (error) {
        const errorMessage = handleSupabaseError(error, 'Google sign in');
        setLastError(errorMessage);
        setConnectionStatus('disconnected');
      }

      return { error };
    } catch (error) {
      const errorMessage = handleSupabaseError(error, 'Google sign in');
      setLastError(errorMessage);
      setConnectionStatus('disconnected');
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    connectionStatus,
    lastError,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    testConnection,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};